import api from "@/lib/api";

// One-tap: connect the TokenPocket wallet, sign CaryPact's login challenge, and
// mint an x-auth-token server-side — no F12, no pasting. The token is stored on
// the server (never returned to the browser).
//
// TokenPocket-ONLY by design: when MetaMask is also installed it tends to win
// the `window.ethereum` slot, which is why users saw MetaMask pop up. We never
// touch `window.ethereum` blindly — we resolve TokenPocket's own provider via
// EIP-6963 (with legacy fall-backs) and drive every request through it.
const shortA = (a) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a || "");
const sameAddr = (a, b) => a && b && a.toLowerCase() === b.toLowerCase();

// --- EIP-6963 provider registry (collision-free multi-wallet discovery) ------
const _announced = [];
if (typeof window !== "undefined") {
  window.addEventListener("eip6963:announceProvider", (e) => {
    const d = e && e.detail;
    if (d && d.info && d.provider && !_announced.some((x) => x.info.uuid === d.info.uuid)) {
      _announced.push(d);
    }
  });
  try { window.dispatchEvent(new Event("eip6963:requestProvider")); } catch { /* noop */ }
}

const isTP = (p) => !!(p && (p.isTokenPocket || p.isTP));

// TokenPocket sometimes never answers a request (locked, or a wedged popup) — a
// request that never settles is exactly the "spinner forever, no popup" bug.
// Bound every wallet call so the UI always recovers with actionable guidance.
const CONNECT_TIMEOUT_MS = 40000;
function withTimeout(promise, ms, code) {
  let t;
  const timeout = new Promise((_, rej) => {
    t = setTimeout(() => { const e = new Error("wallet-timeout"); e.code = code || "TIMEOUT"; rej(e); }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(t));
}

function findTokenPocketSync() {
  if (typeof window === "undefined") return null;
  // 1) EIP-6963 — TokenPocket announces itself with rdns like "pro.tokenpocket".
  const hit = _announced.find((d) => {
    const rdns = (d.info.rdns || "").toLowerCase();
    const name = (d.info.name || "").toLowerCase();
    return rdns.includes("tokenpocket") || name.includes("tokenpocket") || isTP(d.provider);
  });
  if (hit) return hit.provider;
  // 2) TokenPocket's dedicated namespace.
  if (window.tokenpocket && window.tokenpocket.ethereum) return window.tokenpocket.ethereum;
  // 3) Legacy multi-provider array (never blindly window.ethereum → could be MetaMask).
  const eth = window.ethereum;
  if (eth && Array.isArray(eth.providers)) {
    const tp = eth.providers.find(isTP);
    if (tp) return tp;
  }
  // 4) Single injection that explicitly IS TokenPocket.
  if (isTP(eth)) return eth;
  return null;
}

// EIP-6963 announcements are async — if the registry is empty at click time,
// re-request and wait a beat before giving up.
async function resolveTokenPocket() {
  let p = findTokenPocketSync();
  if (p) return p;
  try { window.dispatchEvent(new Event("eip6963:requestProvider")); } catch { /* noop */ }
  for (let i = 0; i < 8 && !p; i++) {
    await new Promise((r) => setTimeout(r, 60));
    p = findTokenPocketSync();
  }
  return p;
}

// Is TokenPocket present in THIS browser right now? (registry + legacy checks)
export function detectTokenPocket() {
  return !!findTokenPocketSync();
}

// Deep links (tpdapp://) only resolve inside the TokenPocket MOBILE app — on a
// desktop extension they do nothing. Gate the "Open in TokenPocket" button on this.
export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
}

// Build a deep link that opens `url` inside TokenPocket's own dapp browser (where
// the wallet is injected). For users who opened the page in a plain browser with
// no wallet — tapping this hands off into TokenPocket so the sync can complete.
export function tokenPocketDeepLink(url) {
  const target = url || (typeof window !== "undefined" ? window.location.href : "");
  const params = { url: target, chain: "ETH", source: "BotChainCP" };
  return `tpdapp://open?params=${encodeURIComponent(JSON.stringify(params))}`;
}

// Open TokenPocket's connect prompt right away, de-duping concurrent clicks and
// riding out a stuck "-32002 already pending" by polling for the approval.
let _pending = null;
async function promptConnect(eth) {
  if (_pending) return _pending;
  _pending = eth.request({ method: "eth_requestAccounts" })
    .catch(async (e) => {
      if (e && (e.code === -32002 || /already pending/i.test(e.message || ""))) {
        for (let i = 0; i < 25; i++) {
          await new Promise((r) => setTimeout(r, 1000));
          try {
            const acc = await eth.request({ method: "eth_accounts" });
            if (acc && acc.length) return acc;
          } catch { /* keep polling */ }
        }
      }
      throw e;
    })
    .finally(() => { _pending = null; });
  return _pending;
}

// Return the accounts TokenPocket shares. We deliberately do NOT call
// `wallet_requestPermissions` to force a picker — that makes TokenPocket fire
// its `otherChainAccountChange` event, which crashes inside TokenPocket's own
// inpage.js ("reading '_bn'") and wedges the flow. `eth_requestAccounts` alone
// pops TokenPocket's connect UI (letting the user pick there) without that side
// effect. TokenPocket exposes only the active account, so if the wanted wallet
// isn't the one connected we let the caller show a friendly "switch to it" note.
async function getAccounts(eth) {
  let accounts = [];
  try { accounts = (await eth.request({ method: "eth_accounts" })) || []; } catch { /* not connected */ }
  if (!accounts.length) accounts = (await promptConnect(eth)) || [];
  return accounts;
}

async function signAndMint(signPath, mintPath, label, expected) {
  const eth = await resolveTokenPocket();
  if (!eth) {
    throw new Error("TokenPocket wasn't detected. Open this page inside the TokenPocket app browser, or install the TokenPocket extension and unlock it, then tap Sync again.");
  }

  let accounts;
  try {
    accounts = await withTimeout(getAccounts(eth), CONNECT_TIMEOUT_MS, "TIMEOUT");
  } catch (e) {
    if (e && e.code === "TIMEOUT") {
      // Last chance: it may have connected without settling the request promise.
      try {
        const late = await eth.request({ method: "eth_accounts" });
        if (late && late.length) { accounts = late; }
      } catch { /* still nothing */ }
      if (!accounts || !accounts.length) {
        _pending = null; // clear any wedged in-flight lock so the next tap is clean
        throw new Error("TokenPocket didn't respond. Make sure TokenPocket is OPEN and UNLOCKED, then look for its approval popup (it can open behind this window). If nothing appears, close any stuck TokenPocket popup, reload this page, and tap Sync again.");
      }
    } else if (e && (e.code === -32002 || /already pending/i.test(e.message || ""))) {
      throw new Error("A TokenPocket connection request is already open — approve (or reject) it in TokenPocket, then tap Sync again. If nothing appears, reload the page.");
    } else if (e && e.code === 4001) {
      throw new Error("You declined the TokenPocket connection. Approve it to sync this wallet.");
    } else {
      throw e;
    }
  }
  if (!accounts || !accounts.length) {
    throw new Error("TokenPocket didn't share an account. Unlock TokenPocket and approve the connection, then tap Sync again.");
  }

  // Sync whatever wallet is selected in TokenPocket. If it isn't the wallet this
  // sync was requested for we DON'T block — we just flag it so the UI can show a
  // friendly heads-up AFTER the sync succeeds. (TokenPocket only exposes its
  // active account, and its signature proves ownership of that account anyway.)
  const match = expected ? accounts.find((a) => sameAddr(a, expected)) : null;
  const address = match || accounts[0];

  const { data: challenge } = await api.post(signPath, { address });
  let signature;
  try {
    signature = await eth.request({ method: "personal_sign", params: [challenge.message, address] });
  } catch (e) {
    throw new Error("You declined the signature in TokenPocket. Approve it to connect this wallet.");
  }
  const { data } = await api.post(mintPath, { address, signature, label: label || null });
  return { ...data, address, expected: expected || null, mismatch: !!(expected && !match) };
}

export async function connectAndMint(label, expected) {
  return signAndMint("/cp-connections/sign-request", "/cp-connections/mint", label, expected);
}

// Public variant for the member-facing /sync page (no login required —
// the wallet signature itself is the proof of ownership).
export async function connectAndMintPublic(expected) {
  return signAndMint("/wallet-sync/sign-request", "/wallet-sync/mint", null, expected);
}

// ---- Wallet sweep ---------------------------------------------------------
// No page can tell TokenPocket to change wallet, so the sweep reacts instead:
// the operator clicks a different wallet in TokenPocket's own panel and we pick
// it up. Returns the address of whatever is active, prompting to connect if needed.
export async function requestAccount() {
  const eth = await resolveTokenPocket();
  if (!eth) {
    throw new Error("TokenPocket wasn't detected. Open this page in a browser where the TokenPocket extension is unlocked (or inside the TokenPocket app), then start the sweep again.");
  }
  const accounts = await withTimeout(getAccounts(eth), CONNECT_TIMEOUT_MS, "TIMEOUT");
  if (!accounts || !accounts.length) {
    throw new Error("TokenPocket didn't share an account. Unlock it, approve the connection, and start the sweep again.");
  }
  return accounts[0];
}

// Fire `cb(address)` whenever the wallet selected in TokenPocket changes.
// `accountsChanged` is the documented event, but TokenPocket doesn't always emit
// it for an in-wallet switch — so we poll alongside it. Returns an unsubscribe.
export function watchActiveAccount(cb, initial) {
  let stopped = false;
  let eth = null;
  let last = initial || null;
  const push = (a) => {
    const addr = Array.isArray(a) ? a[0] : a;
    if (stopped || !addr || (last && sameAddr(last, addr))) return;
    last = addr;
    cb(addr);
  };
  const onChanged = (accs) => push(accs);
  (async () => {
    eth = await resolveTokenPocket();
    if (!eth || stopped) return;
    if (eth.on) eth.on("accountsChanged", onChanged);
    while (!stopped) {
      try {
        const accs = await eth.request({ method: "eth_accounts" });
        if (accs && accs.length) push(accs[0]);
      } catch { /* locked — try again on the next tick */ }
      await new Promise((r) => setTimeout(r, 1500));
    }
  })();
  return () => {
    stopped = true;
    if (eth && eth.removeListener) {
      try { eth.removeListener("accountsChanged", onChanged); } catch { /* noop */ }
    }
  };
}

// One wallet of a sweep: sign CaryPact's challenge with the active wallet and let
// the server open that wallet's session and read its income. Resolves with
// `{ ok: false, reason: "no_carypact_account" }` when CaryPact doesn't know it.
export async function sweepConnect(address, label) {
  return signAndMint("/cp-connections/sign-request", "/wallet-sweep/connect", label || null, address);
}
